import { Injectable, inject, signal } from '@angular/core'
import { Router } from '@angular/router'
import { isTemporaryChatId } from '../utils/temp-chat'
import { MuteService } from 'src/app/services/mute/mute.service'

/**
 * Minimal desktop-notification service. Pure Web Notifications API +
 * localStorage — no Firebase, websockets, or cross-tab/channel dedupe. A
 * `notificationEnabled` setting is reduced to the single case we support:
 * notify when the currently-open chat's reply finishes in a backgrounded tab.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly KEY = 'notificationEnabled'
  private readonly LEGACY_KEY = 'settings.notificationEnabled'
  private readonly LEGACY_SETTINGS_KEY = 'settings'
  private readonly router = inject(Router)
  private readonly mute = inject(MuteService)

  readonly enabled = signal<boolean>(this.readInitialEnabled())

  /** `Notification` is undefined in non-browser/SSR-ish contexts; guard cheaply. */
  get supported(): boolean {
    return typeof Notification !== 'undefined'
  }

  /**
   * Turn the setting on/off. Turning on requests OS permission (once, if not
   * already decided); a refusal reverts the setting to off and reports
   * `denied` so the caller can surface a permission-denied message.
   */
  async setEnabled(on: boolean): Promise<{ ok: boolean; denied?: boolean }> {
    if (!on) {
      this.enabled.set(false)
      this.persist('false')
      return { ok: true }
    }

    if (!this.supported) {
      this.disableAndPersist()
      return { ok: false }
    }

    // Match the Svelte flow: every attempt to enable goes through the browser
    // permission API before changing/persisting UI state. Some browsers throw
    // here (insecure context, embedded webview), which is equivalent to an
    // unavailable/denied permission from the user's perspective.
    let permission: NotificationPermission
    try {
      permission = await Notification.requestPermission()
    } catch {
      this.disableAndPersist()
      return { ok: false }
    }

    if (permission !== 'granted') {
      this.disableAndPersist()
      return { ok: false, denied: permission === 'denied' }
    }

    this.enabled.set(true)
    this.persist('true')
    return { ok: true }
  }

  /**
   * Fire a desktop notification for a finished assistant reply. Gated on the
   * setting being enabled, permission actually granted, AND the tab being
   * backgrounded (`document.hidden`) — callers don't need to re-check any of
   * that themselves.
   *
   * `chatId` makes the notification actionable. Two independent paths, because
   * they cover different lifetimes:
   * - `onclick` routes in-process. Strictly better whenever the page is still
   *   alive: no reload, no bootstrap, keeps app state.
   * - `data.deepLink` is an opaque passthrough in the Notifications spec (the
   *   browser only ever hands it back on the notification object, so it is inert
   *   on the web). The native shell's Notification shim reads it to route a tap
   *   that arrives after the WebView was suspended or killed, when `onclick` no
   *   longer exists. Field name per
   *   native-packaging/04-push-notifications.md §4.1; the shim validates it
   *   against `^/(?!/)[^\s\\]*$`.
   *
   * A temporary chat has no persisted doc, so a cold-start deep link into it
   * would land on an empty conversation — it gets `onclick` only.
   */
  notifyReply(title: string, body: string, chatId?: string): void {
    // The per-chat bell (MuteService) is the user's control. There is no global
    // on/off switch any more: the browser permission is the only other gate.
    if (chatId && this.mute.isMuted(chatId)) return
    if (!this.supported) return
    if (Notification.permission !== 'granted') return
    if (typeof document === 'undefined' || !document.hidden) return

    const deepLink = chatId && !isTemporaryChatId(chatId) ? `/chat/c/${chatId}` : undefined

    try {
      const notification = new Notification(title, {
        body: body.slice(0, 200),
        icon: '/favicon.png',
        ...(deepLink ? { data: { deepLink } } : {}),
      })
      if (chatId) {
        notification.onclick = () => {
          void this.router.navigate(['/chat', 'c', chatId])
          window.focus()
        }
      }
    } catch {
      // ignore — notification is best-effort
    }
  }

  /** A stale persisted opt-in must never render enabled after permission was
   * revoked, or in a browser that does not implement notifications. */
  private readInitialEnabled(): boolean {
    const persisted = this.readPersisted() === 'true'
    if (!persisted) return false
    if (!this.supported || Notification.permission !== 'granted') {
      this.persist('false')
      return false
    }
    return true
  }

  private disableAndPersist(): void {
    this.enabled.set(false)
    this.persist('false')
  }

  private readPersisted(): string | null {
    try {
      const current = localStorage.getItem(this.KEY)
      if (current !== null) return current
      const legacy = localStorage.getItem(this.LEGACY_KEY)
      if (legacy === 'true' || legacy === 'false') {
        localStorage.setItem(this.KEY, legacy)
        return legacy
      }
      const settings = localStorage.getItem(this.LEGACY_SETTINGS_KEY)
      if (settings) {
        const notificationEnabled = JSON.parse(settings)?.notificationEnabled
        if (typeof notificationEnabled === 'boolean') {
          const migrated = String(notificationEnabled)
          localStorage.setItem(this.KEY, migrated)
          return migrated
        }
      }
      return null
    } catch {
      return null
    }
  }

  private persist(value: 'true' | 'false'): void {
    try {
      localStorage.setItem(this.KEY, value)
    } catch {
      // ignore — persistence is best-effort
    }
  }
}
